/**
 * Admin Archive — restore or permanently delete archived studies, and review
 * the deletion audit trail.
 *
 * Backed by admin-gated callables (listArchivedProjects, restoreProject,
 * hardDeleteProject, listDeletionAudit). No client-side admin gate — non-admins
 * get a permission error from the backend, same posture as the Instruction
 * Library / AdminUsers pages.
 *
 * Permanent delete is recursive + audited server-side and requires the admin to
 * type the project name to confirm (irreversible).
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from '../lib/callable';
import { Link } from 'react-router-dom';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { useToast } from '../contexts/ToastContext';
import {
  ArrowLeft,
  Archive,
  RefreshCw,
  RotateCcw,
  Trash2,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// ── Wire types (mirror projectDeletionCallables.ts) ─────────────────────────

interface ArchivedProjectRow {
  id: string;
  name: string;
  archivedAt: number | null;
  archivedBy: string | null;
  createdBy: string | null;
  purgeAt: number | null;
}

interface DeletionAuditEntry {
  id: string;
  projectId: string;
  projectName: string | null;
  action: string;
  actorUid: string | null;
  actorEmail: string | null;
  totalDocs: number | null;
  topLevelDocExisted: boolean | null;
  incomplete: boolean;
  status: string | null;
  startedAt: number | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const ARCHIVE_PAGE_SIZE = 10;
const AUDIT_PAGE_SIZE = 15;

function fmtDate(ms: number | null): string {
  if (ms === null) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function daysUntil(ms: number | null): number | null {
  if (ms === null) return null;
  return Math.ceil((ms - Date.now()) / DAY_MS);
}

const ACTION_STYLES: Record<string, string> = {
  archive: 'bg-amber-100 text-amber-700',
  restore: 'bg-green-100 text-green-700',
  hardDelete: 'bg-red-100 text-red-700',
  autoPurge: 'bg-red-100 text-red-700',
  discardFailed: 'bg-gray-100 text-gray-700',
};

function PaginationBar({
  page,
  pageCount,
  total,
  pageSize,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-col gap-3 border-t border-[#E3E8EA] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm text-[#5F696C]">Showing {start}–{end} of {total}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="inline-flex items-center gap-1 rounded-full border border-[#CAD2D5] px-4 py-2 text-sm font-semibold text-[#35565C] transition hover:border-[var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" /> Previous
        </button>
        <span className="min-w-24 text-center text-sm font-semibold text-[#001820]">Page {page} of {pageCount}</span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(pageCount, page + 1))}
          disabled={page === pageCount}
          className="inline-flex items-center gap-1 rounded-full border border-[#CAD2D5] px-4 py-2 text-sm font-semibold text-[#35565C] transition hover:border-[var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function AdminArchive() {
  const { showToast } = useToast();

  const [projects, setProjects] = useState<ArchivedProjectRow[]>([]);
  const [audit, setAudit] = useState<DeletionAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [archivePage, setArchivePage] = useState(1);
  const [auditPage, setAuditPage] = useState(1);

  // Permanent-delete confirmation modal state.
  const [confirmTarget, setConfirmTarget] = useState<ArchivedProjectRow | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listArchived = httpsCallable(
        functions,
        CLOUD_FUNCTIONS.LIST_ARCHIVED_PROJECTS,
        { timeout: CALLABLE_TIMEOUTS.SHORT },
      );
      const listAudit = httpsCallable(functions, CLOUD_FUNCTIONS.LIST_DELETION_AUDIT, { timeout: CALLABLE_TIMEOUTS.SHORT });
      const [pRes, aRes] = await Promise.all([listArchived({}), listAudit({ limit: 100 })]);
      setProjects((pRes.data as { projects: ArchivedProjectRow[] }).projects || []);
      setAudit((aRes.data as { entries: DeletionAuditEntry[] }).entries || []);
    } catch (e: any) {
      console.error('[AdminArchive] load failed', e);
      setError(e?.message || 'Failed to load the archive. Admin access is required.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const archivePageCount = Math.max(1, Math.ceil(projects.length / ARCHIVE_PAGE_SIZE));
  const safeArchivePage = Math.min(archivePage, archivePageCount);
  const pagedProjects = projects.slice(
    (safeArchivePage - 1) * ARCHIVE_PAGE_SIZE,
    safeArchivePage * ARCHIVE_PAGE_SIZE,
  );
  const auditPageCount = Math.max(1, Math.ceil(audit.length / AUDIT_PAGE_SIZE));
  const safeAuditPage = Math.min(auditPage, auditPageCount);
  const pagedAudit = audit.slice(
    (safeAuditPage - 1) * AUDIT_PAGE_SIZE,
    safeAuditPage * AUDIT_PAGE_SIZE,
  );

  const handleRestore = async (row: ArchivedProjectRow) => {
    setBusyId(row.id);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.RESTORE_PROJECT, { timeout: CALLABLE_TIMEOUTS.SHORT });
      await fn({ projectId: row.id });
      showToast(`"${row.name}" restored to the dashboard.`, 'success');
      await load();
    } catch (e: any) {
      console.error('[AdminArchive] restore failed', e);
      showToast(e?.message || 'Failed to restore project.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handlePermanentDelete = async (row: ArchivedProjectRow) => {
    setBusyId(row.id);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.HARD_DELETE_PROJECT, {
        timeout: CALLABLE_TIMEOUTS.LONG,
      });
      const res = (await fn({ projectId: row.id })).data as {
        incomplete: boolean;
        totalDocs: number;
        remainingDocs: number;
      };
      if (res.incomplete) {
        showToast(
          `Deleted "${row.name}" but ${res.remainingDocs} document(s) remained — check the audit log.`,
          'error',
        );
      } else {
        showToast(
          `Permanently deleted "${row.name}" (${res.totalDocs} document(s) removed).`,
          'success',
        );
      }
      setConfirmTarget(null);
      setConfirmText('');
      await load();
    } catch (e: any) {
      console.error('[AdminArchive] hard delete failed', e);
      showToast(e?.message || 'Failed to permanently delete project.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-[1440px] px-6 py-8 lg:px-10" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/" className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" title="Back to dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Data lifecycle</p>
            <h1 className="flex items-center gap-2 text-3xl font-normal tracking-[-0.035em] text-[#001820]">
              <Archive className="h-6 w-6 text-[#B46B08]" />
              Archived Studies
            </h1>
            <p className="text-gray-600 text-sm mt-0.5">
              Restore an archived study, or permanently delete it. Archived studies are
              automatically deleted 30 days after archiving.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 rounded-full border border-[#CAD2D5] bg-white px-4 py-2.5 text-sm font-semibold text-[#35565C] transition hover:border-[var(--brand-primary)] disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : (
        <>
          {/* Archived studies */}
          <div className="mb-8 overflow-hidden rounded-2xl border border-[#DCE2E5] bg-white shadow-[0_10px_30px_rgba(15,42,50,0.06)]">
            {projects.length === 0 ? (
              <div className="p-10 text-center text-gray-500">
                <Archive className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                No archived studies.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Study</th>
                    <th className="px-4 py-3 font-medium">Archived</th>
                    <th className="px-4 py-3 font-medium">Auto-delete</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedProjects.map((row) => {
                    const days = daysUntil(row.purgeAt);
                    return (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{row.name || '(untitled)'}</div>
                          <div className="text-xs text-gray-400 font-mono">{row.id}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{fmtDate(row.archivedAt)}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs font-medium ${
                              days !== null && days <= 3 ? 'text-red-600' : 'text-gray-600'
                            }`}
                          >
                            {days === null
                              ? '—'
                              : days <= 0
                                ? 'Due now'
                                : `in ${days} day${days === 1 ? '' : 's'}`}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleRestore(row)}
                              disabled={busyId === row.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-green-700 hover:bg-green-50 rounded-lg disabled:opacity-50"
                            >
                              <RotateCcw className="w-4 h-4" />
                              Restore
                            </button>
                            <button
                              onClick={() => {
                                setConfirmTarget(row);
                                setConfirmText('');
                              }}
                              disabled={busyId === row.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {projects.length > ARCHIVE_PAGE_SIZE && (
              <PaginationBar
                page={safeArchivePage}
                pageCount={archivePageCount}
                total={projects.length}
                pageSize={ARCHIVE_PAGE_SIZE}
                onPageChange={setArchivePage}
              />
            )}
          </div>

          {/* Deletion audit feed */}
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent deletions</h2>
          <div className="overflow-hidden rounded-2xl border border-[#DCE2E5] bg-white shadow-[0_10px_30px_rgba(15,42,50,0.06)]">
            {audit.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No deletion activity yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                    <th className="px-4 py-3 font-medium">Study</th>
                    <th className="px-4 py-3 font-medium">By</th>
                    <th className="px-4 py-3 font-medium text-right">Docs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedAudit.map((e) => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {fmtDate(e.startedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            ACTION_STYLES[e.action] || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {e.action}
                        </span>
                        {e.incomplete && (
                          <span
                            className="ml-2 text-xs text-red-600 font-medium"
                            title="Some documents remained after the delete"
                          >
                            incomplete
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{e.projectName || '(untitled)'}</div>
                        <div className="text-xs text-gray-400 font-mono">{e.projectId}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {e.actorEmail || e.actorUid || '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {e.totalDocs ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {audit.length > AUDIT_PAGE_SIZE && (
              <PaginationBar
                page={safeAuditPage}
                pageCount={auditPageCount}
                total={audit.length}
                pageSize={AUDIT_PAGE_SIZE}
                onPageChange={setAuditPage}
              />
            )}
          </div>
        </>
      )}

      {/* Permanent-delete confirmation */}
      {confirmTarget && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Permanently delete</h2>
            </div>
            <p className="text-gray-600 mb-2">
              This permanently and irreversibly deletes <strong>{confirmTarget.name}</strong> and
              all of its data. This cannot be undone.
            </p>
            <p className="text-sm text-gray-600 mb-3">
              Type the study name to confirm:
            </p>
            <input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmTarget.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none mb-6"
            />
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setConfirmTarget(null);
                  setConfirmText('');
                }}
                disabled={busyId === confirmTarget.id}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePermanentDelete(confirmTarget)}
                disabled={
                  busyId === confirmTarget.id ||
                  confirmText.trim() !== confirmTarget.name.trim()
                }
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {busyId === confirmTarget.id ? 'Deleting…' : 'Permanently delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
